import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '../../lib/cn.ts';
import { useSmoothLevel } from './useSmoothLevel.ts';

/**
 * What the voice is doing. `idle` is a call that is over, `connecting` one that
 * has not started; the other three are the states the agent reports while it
 * runs.
 */
export type VoiceOrbState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking';

/*
 * The ring, drawn behind the core rather than around it.
 *
 * It is its own layer because it is the part that fades: opacity on an element
 * fades everything inside it too, and the core sitting inside the ring is what
 * turned a navy orb into a washed-out grey disc on a live call.
 */
const haloVariants = cva('absolute inset-0 rounded-full', {
  variants: {
    state: {
      idle: 'bg-fill-faint opacity-40',
      connecting: 'bg-fill-faint',
      listening: 'bg-fill',
      thinking: 'bg-fill',
      /* Green is the one thing the eye can catch from across a desk. */
      speaking: 'animate-breathe bg-accent-tint',
    },
  },
  defaultVariants: { state: 'idle' },
});

const frameVariants = cva('relative flex items-center justify-center', {
  variants: {
    size: {
      md: 'size-30',
      lg: 'size-49',
      /* A phone keeps the screen for the caption; wider screens get the full orb. */
      responsive: 'size-25 mobile:size-49',
    },
  },
  defaultVariants: { size: 'lg' },
});

/* `relative` only so the core paints over the absolutely positioned ring. */
const coreVariants = cva('relative rounded-full bg-primary', {
  variants: {
    state: {
      idle: '',
      connecting: 'animate-pulse',
      listening: '',
      thinking: 'animate-pulse',
      speaking: '',
    },
    size: {
      md: 'size-24',
      lg: 'size-40',
      responsive: 'size-20 mobile:size-40',
    },
  },
  defaultVariants: { state: 'idle', size: 'lg' },
});

export interface VoiceOrbProps
  extends
    Omit<ComponentPropsWithRef<'span'>, 'style' | 'children'>,
    VariantProps<typeof frameVariants> {
  state: VoiceOrbState;
  /**
   * How loud the voice the orb is standing in for is right now, `0`–`1`.
   *
   * Raw is fine, and raw is what the volume hooks give: the orb smooths it
   * itself. Left out where there is nothing to measure — before a call
   * connects, or in a written conversation. Anything past `1` is a loud room
   * rather than a bigger orb, so it is clamped.
   */
  level?: number;
}

/** How far the core grows between silence and the loudest a voice gets. */
const LEVEL_GROWTH = 0.22;
/** How much of the halo's presence is earned by the voice rather than given. */
const LEVEL_GLOW = 0.35;

/**
 * The one thing on screen during a spoken conversation.
 *
 * It stands in for the other person: a call has no face, and a status line
 * alone leaves the screen looking frozen while somebody is talking. What it
 * says is carried by the line beneath it as words — the orb is decoration and
 * is hidden from screen readers, so nothing depends on seeing it move.
 *
 * Given a `level` it moves with the voice instead of on a timer, which is the
 * difference between a screen that is animated and a screen that is listening:
 * the same orb, growing when somebody speaks louder and settling when they
 * stop, is the only feedback a talker gets that they are being heard at all.
 */
export function VoiceOrb({ state, size, level, className, ...props }: VoiceOrbProps) {
  /*
   * Smoothed here rather than by whoever is holding the microphone: every
   * caller would otherwise have to know that a volume meter read straight onto
   * a shape is a flicker, and one of them would forget.
   */
  const smoothed = useSmoothLevel(level);
  const loudness = smoothed ?? null;

  return (
    <span
      data-slot='voice-orb'
      data-state={state}
      aria-hidden='true'
      className={cn(frameVariants({ size }), className)}
      {...props}
    >
      {/*
       * The halo carries the level as light, not as size: `speaking` already
       * animates its own scale, and a CSS animation beats an inline transform —
       * a second one written here would simply be ignored.
       */}
      <span
        data-slot='voice-orb-halo'
        className={haloVariants({ state })}
        style={
          loudness === null
            ? undefined
            : { opacity: 1 - LEVEL_GLOW + loudness * LEVEL_GLOW }
        }
      />

      {/*
       * The core carries it as size. Written as a transform rather than a
       * width so it costs no layout: the orb is redrawn on every animation
       * frame of a live call, and a size that reflows the column around it at
       * that rate is a stutter, not a visual.
       *
       * No CSS transition on it either. The smoothing above already produces
       * one value per frame, and a transition on top of that is a second
       * animation being restarted before the first one finishes — which is
       * not a slower orb, it is a shaking one.
       */}
      <span
        data-slot='voice-orb-core'
        className={coreVariants({ state, size })}
        style={
          loudness === null
            ? undefined
            : { transform: `scale(${1 + loudness * LEVEL_GROWTH})` }
        }
      />
    </span>
  );
}

export { frameVariants as voiceOrbVariants };
